'use client'

import { useId, useState } from 'react'

import { CheckIcon, ChevronsUpDownIcon, XIcon } from 'lucide-react'

import { Badge } from './badge'
import { Button } from './button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from './command'
import { Label } from './label'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface ComboboxOption {
  value: string
  label: string
}

interface ComboboxMultipleProps {
  options: ComboboxOption[]
  selectedValues: string[]
  onSelectionChange: (values: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  label?: string
  id?: string
  className?: string
}

export const ComboboxMultiple = ({
  options,
  selectedValues,
  onSelectionChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  label,
  id,
  className
}: ComboboxMultipleProps) => {
  const generatedId = useId()
  const comboboxId = id || generatedId
  const [open, setOpen] = useState(false)

  const toggleSelection = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value]
    onSelectionChange(newValues)
  }

  const removeSelection = (value: string) => {
    onSelectionChange(selectedValues.filter(v => v !== value))
  }

  return (
    <div className={`w-full space-y-2 ${className || ''}`}>
      {label && <Label htmlFor={comboboxId}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={comboboxId}
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className='h-auto min-h-10 w-full justify-between hover:bg-transparent'
          >
            <div className='flex flex-wrap items-center gap-1 pr-2.5'>
              {selectedValues.length > 0 ? (
                selectedValues.map(val => {
                  const option = options.find(c => c.value === val)

                  return option ? (
                    <Badge key={val} variant='outline' className='rounded-sm'>
                      {option.label}
                      <Button
                        variant='ghost'
                        size='icon'
                        className='size-4'
                        onClick={e => {
                          e.stopPropagation()
                          removeSelection(val)
                        }}
                        asChild
                      >
                        <span>
                          <XIcon className='size-3' />
                        </span>
                      </Button>
                    </Badge>
                  ) : null
                })
              ) : (
                <span className='text-muted-foreground'>{placeholder}</span>
              )}
            </div>
            <ChevronsUpDownIcon className='text-muted-foreground/80 shrink-0' aria-hidden='true' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[var(--radix-popper-anchor-width)] p-0 z-[2000]' align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {options.map(option => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => toggleSelection(option.value)}
                  >
                    <span className='truncate'>{option.label}</span>
                    {selectedValues.includes(option.value) && <CheckIcon size={16} className='ml-auto' />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface ComboboxSingleProps {
  options: ComboboxOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  label?: string
  id?: string
  className?: string
}

export const ComboboxSingle = ({
  options,
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  label,
  id,
  className
}: ComboboxSingleProps) => {
  const generatedId = useId()
  const comboboxId = id || generatedId
  const [open, setOpen] = useState(false)

  const selectedOption = options.find(option => option.value === value)

  return (
    <div className={`w-full space-y-2 ${className || ''}`}>
      {label && <Label htmlFor={comboboxId}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={comboboxId}
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className='w-full justify-between'
          >
            <span className='truncate'>{selectedOption ? selectedOption.label : placeholder}</span>
            <ChevronsUpDownIcon className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[var(--radix-popper-anchor-width)] p-0 z-[2000]' align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {options.map(option => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onValueChange(option.value === value ? '' : option.value)
                      setOpen(false)
                    }}
                  >
                    <CheckIcon
                      className={`mr-2 h-4 w-4 ${value === option.value ? 'opacity-100' : 'opacity-0'}`}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

